export default {
  created() {
    try {
      window.tpWalletCallback = this.tpWalletCallback;
    } catch (error) {
      console.error('TP wallet initialization error:', error);
    }
  },
  methods: {
    isEVM() {
      try { return typeof window.ethereum !== 'undefined'; } catch (_) { return false; }
    },
    isTRON() {
      try { return typeof window.tronWeb !== 'undefined' || typeof window.tronLink !== 'undefined'; } catch (_) { return false; }
    },
    // 简单的 UTF-8 → 0xhex（若你想要用 hex 时）
    toHex(message) {
      const enc = new TextEncoder();
      const bytes = enc.encode(String(message));
      return '0x' + Array.from(bytes).map(b => b.toString(16).padStart(2,'0')).join('');
    },
      // —— 连接钱包 —— //
    async connectWallet() {
      if (this.isEVM()) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        return accounts && accounts[0];
      }
      if (this.isTRON()) {
        // TronLink/TP 的 TRON 网络一般在 dapp 打开时就已注入并授权
        // 若未授权，可以提示用户手动在 TP 里切换到 TRON 钱包
        const addr = window.tronWeb?.defaultAddress?.base58;
        if (addr) return addr;
        // 某些环境可尝试主动请求（新版本才支持）
        if (window.tronLink?.request) {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
          return window.tronWeb?.defaultAddress?.base58;
        }
      }
      throw new Error('No wallet provider detected');
    },

    // —— 统一签名（自动分流 EVM/TRON） —— //
    async signMessageUnified(message) {
      if (this.isEVM()) {
        const provider = window.ethereum;
        const [from] = await provider.request({ method: 'eth_requestAccounts' });

        // 绝大多数钱包接受纯字符串（UTF-8），不要先转 hex
        // personal_sign 的参数顺序： [message, from]
        try {
          const sig = await provider.request({
            method: 'personal_sign',
            params: [message, from],
          });
          return { address: from, signature: sig, chain: 'evm', method: 'personal_sign' };
        } catch (e1) {
          // 回退 eth_sign，注意顺序：[from, message]
          const sig = await provider.request({
            method: 'eth_sign',
            params: [from, message],
          });
          return { address: from, signature: sig, chain: 'evm', method: 'eth_sign' };
        }
      }

      if (this.isTRON()) {
        // TronLink/TP（TRON）
        const tronWeb = window.tronWeb;
        const from = tronWeb?.defaultAddress?.base58;
        if (!from) throw new Error('Tron wallet not authorized');

        // TP/TronLink 推荐用 signMessageV2（对消息做了链上前缀处理）
        const sig = await tronWeb.trx.signMessageV2(message); 
        //（老版本也可用 tronWeb.trx.sign(message)；但 V2 更标准）
        return { address: from, signature: sig, chain: 'tron', method: 'tron_sign_v2' };
      }

      throw new Error('Unsupported wallet / chain');
    },

    // —— 统一验签（本地快速校验；生产建议后端验签） —— //
    async verifySignatureUnified(message, signature, address, chain, method) {
      if (chain === 'evm') {
        // 尽量用后端验签。前端想要本地校验可以用 ethers.js：
        // ethers.utils.verifyMessage(message, signature) === address
        // 如果你不想引 ethers，可以把校验放后端。
        return true; // 前端直接交给后端校验更稳，这里返回 true 让流程先走通
      }
      if (chain === 'tron') {
        try {
          const ok = await window.tronWeb.trx.verifyMessageV2(message, signature, address);
          return !!ok;
        } catch (e) {
          console.warn('TRON verifyMessageV2 failed', e);
          return false;
        }
      }
      return false;
    },
    /**
     * 检测是否在TP钱包内打开
     */
    isTPWallet() {
      // 增加多种检测TP钱包环境的方法，提高检测的准确性
      try {
        // 检查常见的TP钱包注入对象
        if (typeof window.tpwallet !== 'undefined') return true;
        
        // 检查MetaMask/TP钱包的ethereum对象和特征
        if (typeof window.ethereum !== 'undefined') {
          // 检查isTpWallet标志
          if (window.ethereum.isTpWallet) return true;
          
          // 检查TP钱包特有的属性或方法
          if (window.ethereum.isConnected && window.ethereum.request) {
            // TP钱包在某些环境下可能不会设置isTpWallet标志
            // 但会提供标准的EIP-1193接口
            return true;
          }
        }
        
        // 检查navigator.userAgent中的TP钱包标识
        if (navigator && navigator.userAgent) {
          const userAgent = navigator.userAgent.toLowerCase();
          if (userAgent.includes('tpwallet') || userAgent.includes('tokenpocket')) {
            return true;
          }
        }
        
        return false;
      } catch (error) {
        console.error('Error detecting TP wallet:', error);
        return false;
      }
    },

    /**
     * TP钱包回调函数
     */
    tpWalletCallback(data) {
      console.log('TP wallet callback data:', data);
      // 可以根据具体需求扩展此回调函数
    },

    /**
     * 连接TP钱包
     */
    async connectTPWallet() {
      try {
        if (!this.isTPWallet()) {
          return this.$toast('请在TP钱包内打开');
        }

        const provider = window.ethereum || window.tpwallet;
        
        // 请求用户授权连接钱包
        const accounts = await provider.request({
          method: 'eth_requestAccounts'
        });

        // 获取当前钱包地址
        const currentAddress = accounts[0];
        console.log('Connected TP wallet address:', currentAddress);

        // 保存当前钱包地址到本地
        this.local.set('currentWalletAddress', currentAddress);
        
        return currentAddress;
      } catch (error) {
        console.error('TP wallet connection failed:', error);
        this.$toast('连接钱包失败，请重试');
        return null;
      }
    },

    /**
     * 请求TP钱包签名验证 - 增强移动端兼容性
     */
    async requestTPSignature(message = 'Please sign to verify your identity') {
      try {
        if (!this.isTPWallet()) {
          return this.$toast('请在TP钱包内打开');
        }

        const provider = window.ethereum || window.tpwallet;
        const accounts = await provider.request({
          method: 'eth_accounts'
        });

        if (!accounts || accounts.length === 0) {
          // 如果没有已连接的账户，请求连接
          return this.connectTPWallet();
        }

        const currentAddress = accounts[0];
        
        // 构建签名消息
        const from = currentAddress;
        let signature = null;
        let method = '';
        
        try {
          // 尝试使用personal_sign方法（桌面端常用）
          method = 'personal_sign';
          signature = await provider.request({
            method: method,
            params: [this.encodeMessage(message), from]
          });
        } catch (personalSignError) {
          console.warn('Personal sign failed, trying eth_sign:', personalSignError);
          try {
            // 如果personal_sign失败，尝试使用eth_sign方法（移动端兼容性更好）
            method = 'eth_sign';
            signature = await provider.request({
              method: method,
              params: [from, this.encodeMessage(message)]
            });
          } catch (ethSignError) {
            console.error('Both signing methods failed:', ethSignError);
            throw new Error('签名方法不支持，请更新TP钱包');
          }
        }

        console.log('TP wallet signature (method:', method, '):', signature);
        
        // 验证签名 - 传递使用的签名方法以便在验证时适配
        const isValid = await this.verifyTPSignature(message, signature, currentAddress, method);
        
        if (isValid) {
          this.$toast('签名验证成功');
          return { address: currentAddress, signature };
        } else {
          this.$toast('签名验证失败');
          return null;
        }
      } catch (error) {
        console.error('TP wallet signature failed:', error);
        this.$toast('签名验证失败，请重试');
        return null;
      }
    },

    /**
     * 编码消息
     */
    encodeMessage(message) {
      // 将消息转换为十六进制格式
      return '0x' + Buffer.from(message, 'utf8').toString('hex');
    },

    /**
     * 验证TP钱包签名 - 增强移动端兼容性
     */
    async verifyTPSignature(message, signature, address, signingMethod = 'personal_sign') {
      try {
        // 获取provider
        const provider = window.ethereum || window.tpwallet;
        
        let recoveredAddress = null;
        
        try {
          // 尝试使用personal_ecRecover方法验证签名
          recoveredAddress = await provider.request({
            method: 'personal_ecRecover',
            params: [this.encodeMessage(message), signature]
          });
        } catch (ecrecoverError) {
          console.warn('Personal ecrecover failed, using fallback verification:', ecrecoverError);
          // 提供一个简单的回退验证方法 - 主要用于移动端兼容性
          // 在实际项目中，建议使用服务端进行更安全的签名验证
          // 这里只是一个临时解决方案
          const tempAddress = this.getAddressFromSignature(signature, message);
          if (tempAddress) {
            recoveredAddress = tempAddress;
          } else {
            console.error('Fallback verification also failed');
            return false;
          }
        }
        
        // 比较恢复的地址和签名地址是否一致
        if (recoveredAddress) {
          const result = recoveredAddress.toLowerCase() === address.toLowerCase();
          if (!result) {
            console.warn(`Address mismatch: recovered ${recoveredAddress}, expected ${address}`);
          }
          return result;
        }
        return false;
      } catch (error) {
        console.error('TP wallet signature verification failed:', error);
        return false;
      }
    },
    
    /**
     * 从签名中提取地址的简单实现（用于移动端兼容性）
     * 注意：这只是一个回退方案，实际项目中应优先使用服务端验证
     */
    getAddressFromSignature(signature, message) {
      try {
        console.log('Using fallback address extraction for signature verification');
        // 这里可以实现一个基本的从签名中提取地址的逻辑
        // 由于没有web3库，这只是一个简化版本
        // 在实际项目中，建议后端进行签名验证
        return null;
      } catch (error) {
        console.error('Fallback address extraction failed:', error);
        return null;
      }
    },

    /**
     * 验证钱包地址是否与用户绑定的USDT地址相同
     * 注意：此方法需要在获取token后调用
     */
    async verifyWalletAddressWithToken(walletAddress) {
      try {
        // 获取用户绑定的USDT地址
        const memberInfo = await this.getUserInfoWithToken();
        
        if (!memberInfo || !memberInfo.usdt_address) {
          this.$toast('您还未绑定USDT地址');
          return false;
        }

        const boundUsdtAddress = memberInfo.usdt_address.toLowerCase();
        const currentWalletAddress = walletAddress.toLowerCase();
        
        // 比较当前钱包地址与绑定的USDT地址是否相同
        if (currentWalletAddress === boundUsdtAddress) {
          console.log('Wallet address matches bound USDT address');
          return true;
        } else {
          console.log('Wallet address does not match bound USDT address');
          this.$toast('当前钱包地址与绑定的USDT地址不匹配');
          return false;
        }
      } catch (error) {
        console.error('Wallet address verification failed:', error);
        this.$toast('地址验证失败，请重试');
        return false;
      }
    },

    /**
     * 获取用户信息（带token）
     * 注意：此方法需要在获取token后调用
     */
    async getUserInfoWithToken() {
      // 使用Promise包装API调用，便于异步处理
      return new Promise((resolve, reject) => {
        this.$get({
          url: `/member/getMemberInfo`,
          loading: true,
          success: (res) => {
            resolve(res.data);
          },
          error: (err) => {
            console.error('Get user info failed:', err);
            reject(err);
          },
          tip: () => {
            reject(new Error('获取用户信息失败'));
          }
        });
      });
    },

    /**
     * 完整的TP钱包验证流程
     */
    async completeTPWalletVerification() {
      try {
        // 1. 检测是否在TP钱包内打开
        if (!this.isTPWallet()) {
          this.$toast('请在TP钱包内打开');
          return { success: false, reason: 'not_in_tp_wallet' };
        }

        // 2. 连接TP钱包
        const walletAddress = await this.connectTPWallet();
        if (!walletAddress) {
          return { success: false, reason: 'connection_failed' };
        }

        // 3. 请求签名验证
        const signatureResult = await this.requestTPSignature('Login verification for ' + new Date().toISOString());
        if (!signatureResult) {
          return { success: false, reason: 'signature_failed' };
        }

        // 验证成功 - 注意：此时不进行地址验证，因为还没有token
        // 地址验证会在获取token后在login.vue的handleLoginSuccess中进行
        return { success: true, address: walletAddress, signature: signatureResult.signature };
      } catch (error) {
        console.error('Complete TP wallet verification failed:', error);
        this.$toast('TP钱包验证失败，请重试');
        return { success: false, reason: 'verification_failed', error: error.message };
      }
    },
  }
}